import React, { useState } from "react";
import {
  Calendar,
  Store,
  TestTube,
  MessageCircle,
  Phone,
  Stethoscope,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../Button";
import Card from "../Card/Card";
import { sendMessageToParent } from "src/helpers/common";
import { APP_LINKS, POST_MESSAGE_EVENTS } from "src/constants/common";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";
import CallCounselorBottomSheet from "../bottom-sheet/CallCounselorBottomSheet";
import { useAppSelector } from "src/redux/hooks";
import { CONSULTATION_MODULES } from "src/constants/consultation";
import moment from "moment";

interface ServicesCardProps {
  id: string;
  title: string;
  description: string;
  supportProgramKey: string;
  services: Array<{
    id: string;
    title?: string;
    description?: string;
  }>;
}

const ServicesCard: React.FC<ServicesCardProps> = ({
  id,
  title,
  description,
  supportProgramKey,
  services,
}) => {
  const navigate = useNavigate();
  const {
    patient: { data: patientData },
  } = useAppSelector((state) => state.auth);
  const params = useParams();
  const screenName = params["screen-name"];
  const [
    showCallTelecounselorBottomSheet,
    setShowCallTelecounselorBottomSheet,
  ] = useState(false);

  const handleBookAppointment = () => {
    // Event: BOOK_APPOINTMENT_CLICKED
    analytics.trackEvent(EVENT_NAMES.BOOK_APPOINTMENT_CLICKED, {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      support_program_key: supportProgramKey,
      card_title: title,
    });

    // Navigate to book appointment screen
    navigate(`/app/${supportProgramKey}/book-consultation`, {
      state: {
        screenName: screenName,
      },
    });
  };

  const handleChat = () => {
    // Event: CHAT_CLICKED
    analytics.trackEvent(EVENT_NAMES.CHAT_CLICKED, {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      support_program_key: supportProgramKey,
      card_title: title,
    });

    // Navigate to chat screen
    navigate(`/app/${supportProgramKey}/chat-screen`);
  };

  const handleBookLabTests = () => {
    // Event: BOOK_LAB_TESTS_CLICKED
    analytics.trackEvent(EVENT_NAMES.BOOK_LAB_TESTS_CLICKED, {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      support_program_key: supportProgramKey,
      card_title: title,
    });

    // Navigate to book lab tests screen
    navigate(`/app/${supportProgramKey}/book-lab-test`);
  };

  const handleHealthStore = () => {
    // Event: HEALTH_STORE_CLICKED
    analytics.trackEvent(EVENT_NAMES.HEALTH_STORE_CLICKED, {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      support_program_key: supportProgramKey,
      card_title: title,
    });

    // Navigate to health store screen
    // sendMessageToParent(POST_MESSAGE_EVENTS.OPEN_EXTERNAL_LINK, {
    //   link: APP_LINKS.HEALTH_STORE_LINK,
    // })
    sendMessageToParent(POST_MESSAGE_EVENTS.NAVIGATE_TO_WEBVIEW, {
      url: APP_LINKS.HEALTH_STORE_LINK,
      title: "Health Store",
    });
  };

  const handleCallTelecounselor = () => {
    // Event: CALL_TELECOUNSELOR_CLICKED
    analytics.trackEvent(EVENT_NAMES.CALL_TELECOUNSELOR_CLICKED, {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      support_program_key: supportProgramKey,
      card_title: title,
    });

    // Navigate to call telecounselor screen
    //navigate(`/app/${supportProgramKey}/call-telecounselor`);
    setShowCallTelecounselorBottomSheet(true);
  };

  const handleClinicalAppointments = () => {
    // Event: CLINICAL_APPOINTMENTS_CLICKED
    analytics.trackEvent(EVENT_NAMES.CLINICAL_APPOINTMENTS_CLICKED, {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      support_program_key: supportProgramKey,
      card_title: title,
    });

    // Navigate to clinical appointments screen
    // Navigate to visit sdk screen
    const payload = {
      memberId: patientData?.patient_id, // "ABCH23457AW",
      phone: patientData?.contact_no, // "7689313820",
      name: patientData?.name, // "Kiran Venkatesh",
      dob: patientData?.dob, // "1995-10-05",
      gender: patientData?.gender === "M" ? "Male" : "Female", // "Male",
      moduleName: CONSULTATION_MODULES.CLINIC_CONSULTATION,
      planId:
        patientData?.active_plan_details?.plan_duration_meta_info
          ?.visitPolicyNumber, //patientData?.active_plan_details?.plan_id, // "TATVA-OPD", // "ANYRA"
      policyNumber: patientData?.active_plan_details?.patient_plan_rel_id, // "POL820251489026", // ""
      policyStartDate: moment(
        patientData?.active_plan_details?.start_date
      ).format("YYYY-MM-DD"), // "2025-01-01",
      policyEndDate: moment(
        patientData?.active_plan_details?.expiry_date
      ).format("YYYY-MM-DD"), // "2028-01-01",
    };

    sendMessageToParent(POST_MESSAGE_EVENTS.NAVIGATE_TO_VISIT_SDK, payload);
  };

  const serviceDefinitions: Record<
    string,
    {
      icon: React.ReactNode;
      defaultTitle: string;
      defaultDescription: string;
      ctaText: string;
      onClick: () => void;
    }
  > = {
    "clinical-appointments": {
      icon: <Stethoscope className="w-4 h-4 text-secondary" />,
      defaultTitle: "Clinical Appointments",
      defaultDescription: "Schedule visits with Doctors",
      ctaText: "Book",
      onClick: handleClinicalAppointments,
    },
    "book-appointments": {
      icon: <Calendar className="w-4 h-4 text-secondary" />,
      defaultTitle: "Book Appointments",
      defaultDescription: "With Our Expert Health Coaches",
      ctaText: "Book",
      onClick: handleBookAppointment,
    },
    chat: {
      icon: <MessageCircle className="w-4 h-4 text-secondary" />,
      defaultTitle: "Chat",
      defaultDescription: "Get instant support and answers",
      ctaText: "Chat",
      onClick: handleChat,
    },
    "book-lab-tests": {
      icon: <TestTube className="w-4 h-4 text-secondary" />,
      defaultTitle: "Book Lab Tests",
      defaultDescription: "At discounted prices",
      ctaText: "Book",
      onClick: handleBookLabTests,
    },
    "health-store": {
      icon: <Store className="w-4 h-4 text-secondary" />,
      defaultTitle: "Health Store",
      defaultDescription: "Buy essentials health products",
      ctaText: "Shop",
      onClick: handleHealthStore,
    },
    "call-telecounselor": {
      icon: <Phone className="w-4 h-4 text-secondary" />,
      defaultTitle: "Call your Telecounselor",
      defaultDescription:
        "Call your Telecounselor to get instant support and answers",
      ctaText: "Call",
      onClick: handleCallTelecounselor,
    },
  };

  return (
    <div className="mx-4" id={id}>
      <Card
        title={title}
        description={description}
        icon={<Calendar className="w-4 h-4" />}
      >
        <div className="flex flex-col">
          {(services ?? []).map((service, idx) => {
            const def = serviceDefinitions[service.id];
            if (!def) return null;

            return (
              <div
                key={`${service.id}-${idx}`}
                className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-lg transition-colors p-2"
              >
                <div className="flex items-center gap-2">
                  <div className="text-[var(--app-primary)] bg-[var(--icon-bg-primary)] rounded-full p-2">
                    {def.icon}
                  </div>
                  <div>
                    <div className="text-[var(--card-title-color)] text-sm font-medium">
                      {service.title || def.defaultTitle}
                    </div>
                    <div className="text-xs text-[var(--card-desc-color)] font-thin">
                      {service.description || def.defaultDescription}
                    </div>
                  </div>
                </div>
                <Button size="sm" rounded="full" onClick={def.onClick}>
                  {def.ctaText}
                </Button>
              </div>
            );
          })}
        </div>
      </Card>
      {showCallTelecounselorBottomSheet && (
        <CallCounselorBottomSheet
          setShowBottomSheet={setShowCallTelecounselorBottomSheet}
          supportProgramKey={supportProgramKey}
          title={title}
          number="+91 9876543210"
        />
      )}
    </div>
  );
};

export default ServicesCard;
